<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UForm ref="form" :schema="schema" :state="state" :validate-on="['submit']" class="h-full" @submit="onSubmit">
            <UCard>
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">Edit Patient</h3>
                        <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                    </div>
                </template>
                <div class="space-y-6">
                    <UFormGroup name="first_name" label="First Name" required>
                        <UInput v-model="state.first_name" type="text" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="last_name" label="Last Name" required>
                        <UInput v-model="state.last_name" type="text" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="birth_date" label="Date of Birth">
                        <UInput v-model="state.birth_date" type="date" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="gender" label="Gender">
                        <USelectMenu v-model="state.gender" :options="GENDER_OPTIONS" value-attribute="value" option-attribute="label" size="xl"
                            placeholder="Select..." />
                    </UFormGroup>
                    <UFormGroup name="blood_type" label="Blood Type">
                        <USelectMenu v-model="state.blood_type" :options="BLOOD_TYPES" size="xl" placeholder="Select..." />
                    </UFormGroup>
                    <UFormGroup name="phone" label="Phone" help="Leave empty if unknown.">
                        <InputPhone v-model="state.phone" size="xl" />
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton size="lg" color="white" label="Cancel" @click="dismiss()" />
                        <UButton type="submit" size="lg" color="black" label="Save" :loading="saving" />
                    </div>
                </template>
            </UCard>
        </UForm>
    </USlideover>
</template>

<script setup lang="ts">
import { z } from 'zod'
import { normalizeStoredPhone } from '~/utils/phone'
import { BLOOD_TYPES, GENDER_OPTIONS, type FieldPostPatient } from '~/types/fieldPost'

const props = defineProps<{
    open: boolean
    interventionId: string
    patient?: FieldPostPatient
}>()

const emit = defineEmits<{
    'update:open': [boolean]
    'saved': [FieldPostPatient]
}>()

const toast = useToast()

const form = ref()
const saving = ref(false)
const state = reactive({
    first_name: '',
    last_name: '',
    birth_date: '',
    gender: null as 'male' | 'female' | null,
    blood_type: null as string | null,
    phone: '',
})

const schema = z.object({
    first_name: z.string().trim().min(1, 'First name is required.'),
    last_name: z.string().trim().min(1, 'Last name is required.'),
    birth_date: z.string().optional(),
    gender: z.enum(['male', 'female']).nullable().optional(),
    blood_type: z.string().nullable().optional(),
    phone: z.string().optional(),
})

const FIELD_ERRORS: Record<string, { path: string; message: string }[]> = {
    NAME_REQUIRED: [
        { path: 'first_name', message: 'First name is required.' },
        { path: 'last_name', message: 'Last name is required.' },
    ],
    INVALID_PHONE: [{ path: 'phone', message: 'Enter a valid phone number.' }],
}

watch(() => props.open, (open) => {
    if (!open) return
    state.first_name = props.patient?.first_name ?? ''
    state.last_name = props.patient?.last_name ?? ''
    state.birth_date = props.patient?.birth_date ?? ''
    state.gender = props.patient?.gender ?? null
    state.blood_type = props.patient?.blood_type ?? null
    state.phone = normalizeStoredPhone(props.patient?.phone)
})

async function onSubmit() {
    saving.value = true
    try {
        const { patient } = await $fetch<{ patient: FieldPostPatient }>(`/api/field-posts/patients/${props.interventionId}`, {
            method: 'PATCH',
            body: {
                first_name: state.first_name.trim(),
                last_name: state.last_name.trim(),
                birth_date: state.birth_date,
                gender: state.gender ?? '',
                blood_type: state.blood_type ?? '',
                phone: state.phone.trim(),
            },
        })
        emit('saved', patient)
        toast.add({ title: 'Patient updated.', color: 'green', icon: 'i-ph-check-circle-duotone' })
        dismiss()
    } catch (e: any) {
        const code = e?.data?.message as string | undefined
        if (code && FIELD_ERRORS[code]) {
            form.value?.setErrors(FIELD_ERRORS[code])
        } else {
            toast.add({
                title: 'Could not update the patient.',
                description: code === 'NOT_FIELD_POST_PATIENT' ? 'This patient has an app account. Edit them from Customers.' : undefined,
                color: 'red',
                icon: 'i-ph-x-circle-duotone',
            })
        }
    } finally {
        saving.value = false
    }
}

function dismiss() {
    emit('update:open', false)
}
</script>
